import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../App';
import WizardSteps from '../components/WizardSteps';
import TariffStepDetails from '../components/TariffStepDetails';
import TariffStepPricing from '../components/TariffStepPricing';
import TariffStepReview from '../components/TariffStepReview';
import { TARIFF_STEP_LABELS } from '../config/tariffFields';

const API = 'https://api.cpms-srlkevcharge.com';

export default function AddTariff() {
  const navigate = useNavigate();
  const { showToast } = useAppContext();

  const [currentStep, setCurrentStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [organisations, setOrganisations] = useState([]);

  // ── Step 1 state ──────────────────────────────────────────────────────
  const [organisationMode, setOrganisationMode] = useState('existing');
  const [organisationId, setOrganisationId] = useState('');
  const [organisationName, setOrganisationName] = useState('');
  const [selectedFields, setSelectedFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  // ── Step 2 state ──────────────────────────────────────────────────────
  const [priceComponents, setPriceComponents] = useState([
    { tempId: Date.now(), formData: {}, selectedFields: [], errors: {} }
  ]);
  const [includeIdleFee, setIncludeIdleFee] = useState(false);
  const [idleFee, setIdleFee] = useState({ idle_time: '', idle_price: '', grace_period: '' });

  React.useEffect(() => {
    axios.get(`${API}/organisations/`)
      .then(res => setOrganisations(res.data))
      .catch(() => {});
  }, []);

  const validateStep1 = () => {
    const errs = {};
    if (organisationMode === 'existing' && !organisationId) errs.organisation = 'Please select an organisation';
    if (organisationMode === 'new' && !organisationName.trim()) errs.organisation = 'Please enter a name for the new organisation';
    if (!formData.name?.trim()) errs.name = 'Tariff name is required';
    if (!formData.currency) errs.currency = 'Currency is required';
    if (!formData.status) errs.status = 'Tariff status is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = () => {
    if (priceComponents.length === 0) {
      showToast('Add at least one price component to continue', 'error');
      return false;
    }
    let valid = true;
    setPriceComponents(prev => prev.map(c => {
      const errs = {};
      if (!c.formData.charging_unit) errs.charging_unit = 'Unit is required';
      if (!c.formData.price_per_unit?.trim()) errs.price_per_unit = 'Amount can only be a number';
      if (Object.keys(errs).length > 0) valid = false;
      return { ...c, errors: errs };
    }));
    return valid;
  };

  const goNext = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    setCurrentStep(prev => Math.min(prev + 1, 3));
  };

  const goBack = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const handleGoLive = async () => {
    setSubmitting(true);

    const payload = {
      name: formData.name,
      currency: formData.currency,
      status: formData.status,
      description: formData.description || null,
      tariff_code: formData.tariff_code || null,
      effective_from: formData.effective_from || null,
      effective_to: formData.effective_to || null,
      price_components: priceComponents.map(c => ({
        charging_unit: c.formData.charging_unit,
        price_per_unit: c.formData.price_per_unit,
        tax_percent: c.formData.tax_percent || null,
        minimum_charge: c.formData.minimum_charge || null,
        billing_increment: c.formData.billing_increment || null,
      })),
      idle_fee: includeIdleFee ? idleFee : null
    };

    if (organisationMode === 'existing') {
      payload.organisation_id = Number(organisationId);
    } else {
      payload.organisation_name = organisationName.trim();
    }

    try {
      const res = await axios.post(`${API}/tariffs/`, payload);
      showToast(`${res.data.name} tariff created successfully`);
      navigate('/tariffs');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to create tariff', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button className="back-link" onClick={() => navigate('/tariffs')}>← Back to Tariffs</button>

      <div className="page-header">
        <h1>Create Tariff</h1>
        <p>Create a new pricing structure that can be applied to any charging station</p>
      </div>

      <WizardSteps steps={TARIFF_STEP_LABELS} currentStep={currentStep} />

      {currentStep === 1 && (
        <TariffStepDetails
          organisationMode={organisationMode} setOrganisationMode={setOrganisationMode}
          organisationId={organisationId} setOrganisationId={setOrganisationId}
          organisationName={organisationName} setOrganisationName={setOrganisationName}
          selectedFields={selectedFields} setSelectedFields={setSelectedFields}
          formData={formData} setFormData={setFormData}
          errors={errors}
        />
      )}

      {currentStep === 2 && (
        <TariffStepPricing
          priceComponents={priceComponents} setPriceComponents={setPriceComponents}
          includeIdleFee={includeIdleFee} setIncludeIdleFee={setIncludeIdleFee}
          idleFee={idleFee} setIdleFee={setIdleFee}
        />
      )}

      {currentStep === 3 && (
        <TariffStepReview
          organisationMode={organisationMode}
          organisationId={organisationId}
          organisationName={organisationName}
          organisations={organisations}
          formData={formData}
          priceComponents={priceComponents}
          includeIdleFee={includeIdleFee}
          idleFee={idleFee}
        />
      )}

      <div className="wizard-actions">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={currentStep === 1 ? () => navigate('/tariffs') : goBack}
        >
          {currentStep === 1 ? 'Cancel' : '← Back'}
        </button>

        {currentStep < 3 ? (
          <button type="button" className="btn btn-green" onClick={goNext}>
            Proceed To Next Step →
          </button>
        ) : (
          <button type="button" className="btn btn-green" onClick={handleGoLive} disabled={submitting}>
            {submitting ? 'Publishing...' : 'Go Live'}
          </button>
        )}
      </div>
    </>
  );
}